import { useCallback } from 'react'
import { runInAction } from 'mobx'
import { useQueryClient } from 'react-query'

import { useCreateCrossPosts } from 'hooks/queries/useCreateCrossPosts'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { usePostPreviewStore } from '../PostPreviewStoreProvider'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useRootStore } from 'stores/RootStore'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { crossPostGroupKeys } from 'hooks/queries/crossPostGroups/crossPostGroupKeys'

import type { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { AlmanacFile, AlmanacFileSummary } from 'types'
import { PostState, RuntimeStatus } from '../types'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { ScheduledFlowMode } from 'components/PostPreviewModalV2/PostPreviewStore'
import { useCreateCrossPostGroup } from 'hooks/queries/crossPostGroups/useCreateCrossPostGroup'
import { sendToApi } from 'api/client'

export type AccountTarget = {
  account: ExternalIntegration
  fileId: string
  scheduledPostId: number
  postUrl?: string
  isOriginalFile?: boolean
}

export type FileTarget = {
  provider: ExternalIntegrationEnum
  file: AlmanacFile
}

/**
 * Hook that creates all the file/account "targets" required to fulfill the
 * user's action (publish-now, schedule, reminder, draft) and keeps the MobX
 * store in sync while the cross-post API is running.
 *
 * ‣ NORMAL flows (NEW, EDIT) keep the existing origin file intact and only
 *   generate new files for *additional* accounts/providers.
 * ‣ CROSSPOST flow generates a *brand-new* file for **every** selected
 *   account/provider—including the origin—so nothing touches the original.
 * ‣ WIZARD flow always creates a file for the origin channel, even if no account is connected.
 */
export function usePostFilesCreator() {
  const store = usePostPreviewStore()
  const rootStore = useRootStore()
  const { data: kit } = useBrandKit(store.selectedBrandKitId)
  const { mutateAsync: createCrossPosts } = useCreateCrossPosts()
  const { scheduledPost } = usePostPreviewContext()
  const { mutateAsync: createCrossPostGroup } = useCreateCrossPostGroup()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const tempIdFor = (key: string | number) => `pending-${key}`

  const createTargets = useCallback(
    async (
      content: string,
      sourceType: string,
      sourceId: string,
    ): Promise<{ accountTargets: AccountTarget[]; fileTargets: FileTarget[] }> => {
      const crossPostGroup = await createCrossPostGroup()
      const { selectedAccounts, selectedChannels, flow, target, channel } = store

      if (!selectedAccounts.length && flow !== ScheduledFlowMode.WIZARD) {
        return { accountTargets: [], fileTargets: [] }
      }

      const [origin, ...linkedOthers] = selectedAccounts
      const isCrossPost = flow === ScheduledFlowMode.CROSSPOST
      const isWizard = flow === ScheduledFlowMode.WIZARD

      const kitProviders = new Set<ExternalIntegrationEnum>(
        kit?.brand_kit.external_integrations.map((i) => i.provider) ?? [],
      )

      const unlinkedProviders = [...selectedChannels].filter((p) => !kitProviders.has(p))

      // In WIZARD flow, ALWAYS ensure the origin channel is included
      if (isWizard && channel) {
        const originProvider = providerFromChannelType[channel] as ExternalIntegrationEnum
        if (originProvider) {
          // Check if origin already has an account selected
          const originHasAccount = selectedAccounts.some((acc) => acc.provider === originProvider)

          // If origin doesn't have an account, add it to unlinked providers
          if (!originHasAccount && !unlinkedProviders.includes(originProvider)) {
            unlinkedProviders.unshift(originProvider) // Add at the beginning to maintain origin position
          }
        }
      }

      let originStates: PostState[] = []
      if (isCrossPost || isWizard) {
        if (selectedAccounts.length > 0) {
          originStates = [
            {
              provider: origin.provider,
              account: origin,
              fileId: tempIdFor(origin.id),
              runtime: 'creating' as RuntimeStatus,
              result: null,
            },
          ]
        }
      } else {
        originStates = [
          {
            provider: origin.provider,
            account: origin,
            fileId: sourceId,
            runtime: 'creating' as RuntimeStatus,
            result: null,
            isOriginalFile: true,
          },
        ]
      }

      const creating: PostState[] = [
        ...originStates,
        ...linkedOthers.map((acc) => ({
          provider: acc.provider,
          account: acc,
          fileId: tempIdFor(acc.id),
          runtime: 'creating' as RuntimeStatus,
          result: null,
        })),
        ...unlinkedProviders.map((p) => ({
          provider: p,
          fileId: tempIdFor(p),
          runtime: 'creating' as RuntimeStatus,
          result: null,
        })),
      ]

      runInAction(() => {
        store.replacePostStates(creating)
      })

      const cross_post_accounts = [
        ...selectedAccounts
          .filter((_, idx) => (isCrossPost && idx === 0) || idx !== 0)
          .map((acc) => ({
            provider: acc.provider,
            external_integration_id: acc.id,
            output_type: target,
          })),
        ...unlinkedProviders.map((p) => ({
          provider: p,
          output_type: target,
        })),
      ]

      if (!cross_post_accounts.length) {
        return {
          accountTargets: [
            {
              account: origin,
              fileId: sourceId,
              scheduledPostId: scheduledPost?.id as number,
              isOriginalFile: true,
            },
          ],
          fileTargets: [],
        }
      }

      await sendToApi<{ almanac_file: AlmanacFileSummary }>(`/almanac_files/${sourceId}`, {
        method: 'PUT',
        body: { cross_post_group_id: crossPostGroup.cross_post_group.id },
      })

      // Fetch the updated file to ensure the store has the newest value
      await rootStore.files.fetch(sourceId)

      let files: AlmanacFile[] = []
      try {
        files = await createCrossPosts({
          content,
          source_type: sourceType,
          source_id: sourceId,
          cross_post_accounts,
          cross_post_group_id: crossPostGroup.cross_post_group.id,
        })

        if (isWizard && sourceId && selectedAccounts.length > 0) {
          const response = await MediaFileClient.getAlmanacFile(sourceId)
          if (response.type === SUCCESS_RESPONSE_TYPE) {
            const existingFile = response.data
            files.unshift(existingFile.almanac_file)
          }
        }
      } catch (err) {
        runInAction(() =>
          store.postStates.forEach((ps) =>
            store.updatePostState(ps.fileId, {
              runtime: 'failure',
              error: err instanceof Error ? err.message : 'Failed to create cross posts',
            }),
          ),
        )
        throw err
      }

      const accountTargets: AccountTarget[] = []
      const fileTargets: FileTarget[] = []
      const usedFileIds = new Set<number | string>()

      if (!isCrossPost && !isWizard) {
        accountTargets.push({
          account: origin,
          fileId: sourceId,
          scheduledPostId: scheduledPost?.id as number,
          isOriginalFile: true,
        })
      }

      selectedAccounts.forEach((acc, idx) => {
        if (!isCrossPost && !isWizard && idx === 0) return

        const file = files.find((f) => f.scheduled_post?.external_integration?.id === acc.id)
        const placeholderId = tempIdFor(acc.id)

        if (file?.id && file.scheduled_post_id) {
          usedFileIds.add(file.id)
          accountTargets.push({
            account: acc,
            fileId: file.id,
            scheduledPostId: file.scheduled_post_id,
          })
          runInAction(() => store.updatePostState(placeholderId, { fileId: file.id }))
        } else {
          runInAction(() =>
            store.updatePostState(placeholderId, { runtime: 'failure', error: 'Failed to create post' }),
          )
        }
      })

      /* Resolve drafts/reminders for unlinked providers */
      unlinkedProviders.forEach((p) => {
        const file = files.find(
          (f) =>
            !usedFileIds.has(f.id) &&
            !f.scheduled_post?.external_integration &&
            f.channel &&
            providerFromChannelType[f.channel] === p,
        )
        const placeholderId = tempIdFor(p)

        if (file?.id) {
          usedFileIds.add(file.id)
          fileTargets.push({ provider: p, file })
          runInAction(() => store.updatePostState(placeholderId, { fileId: file.id }))
        } else {
          runInAction(() =>
            store.updatePostState(placeholderId, { runtime: 'failure', error: 'Failed to create post' }),
          )
        }
      })

      // Invalidate crossPostGroup query if there is one
      if (crossPostGroup?.cross_post_group?.id) {
        queryClient.invalidateQueries(
          crossPostGroupKeys.crossPostGroup(workspaceId, crossPostGroup.cross_post_group.id),
        )
      }

      return { accountTargets, fileTargets }
    },
    [
      createCrossPostGroup,
      store,
      kit?.brand_kit.external_integrations,
      rootStore.files,
      scheduledPost?.id,
      createCrossPosts,
      queryClient,
      workspaceId,
    ],
  )

  return { createTargets }
}
